import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Code, Inbox, Eye, QrCode, ExternalLink, Copy, Download, BarChart3, LayoutDashboard, TrendingUp, MousePointerClick, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, dt } from "../components/common";
import { Modal, Field, Selecter, TextArea } from "./crm/crmui";

const TIPOS = ["texto", "email", "telefone", "textarea", "select", "checkbox", "produto"];
const PAPEIS = ["nome", "email", "telefone", "organizacao", "mensagem", "produto", "outro"];
const OPERADORES = [["", "sempre visivel"], ["eq", "igual a"], ["ne", "diferente de"], ["preenchido", "estiver preenchido"], ["vazio", "estiver vazio"]];
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const emptyForm = {
  nome: "", titulo_publico: "Fale connosco", descricao: "", cor: "#0B2545",
  mensagem_sucesso: "Obrigado! A sua mensagem foi enviada.",
  campos: [
    { key: "nome", tipo: "texto", label: "Nome", papel: "nome", obrigatorio: true, opcoes: [], condicao: null },
    { key: "email", tipo: "email", label: "Email", papel: "email", obrigatorio: true, opcoes: [], condicao: null },
  ],
  emails_destino: "", auto_reply_ativo: true,
  auto_reply_texto: "Recebemos o seu contacto e vamos responder-lhe em breve.",
  regras_fila: [], fila_default_id: "", campanhas: [], ativo: true,
};

export default function Forms() {
  const [tab, setTab] = useState("dashboard");
  const [forms, setForms] = useState([]);
  const [queues, setQueues] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [overview, setOverview] = useState(null);
  const [ovDesde, setOvDesde] = useState("");
  const [ovAte, setOvAte] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [snippet, setSnippet] = useState(null);
  const [share, setShare] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [subs, setSubs] = useState(null);
  const [queue, setQueue] = useState({ nome: "", membros: "" });
  const [showQueue, setShowQueue] = useState(false);

  const load = async () => {
    try {
      const [f, q] = await Promise.all([api.get("/forms"), api.get("/forms/queues")]);
      setForms(f.data); setQueues(q.data);
      api.get("/cpq/products").then((r) => setProdutos(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadOverview = async () => {
    try {
      const params = {};
      if (ovDesde) params.desde = ovDesde;
      if (ovAte) params.ate = ovAte;
      const { data } = await api.get("/forms/analytics/overview", { params });
      setOverview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { if (tab === "dashboard") loadOverview(); }, [tab, ovDesde, ovAte]); // eslint-disable-line

  const openNew = () => { setForm(emptyForm); setShow(true); };
  const openEdit = (f) => {
    setForm({ ...f, emails_destino: (f.emails_destino || []).join(", "), regras_fila: f.regras_fila || [], campanhas: f.campanhas || [], campos: (f.campos || []).map((c) => ({ ...c, condicao: c.condicao || null })) });
    setShow(true);
  };

  const setCampo = (i, k, v) => setForm({ ...form, campos: form.campos.map((c, idx) => idx === i ? { ...c, [k]: v } : c) });
  const setCampoCond = (i, k, v) => setForm({ ...form, campos: form.campos.map((c, idx) => idx === i ? { ...c, condicao: { campo: "", operador: "", valor: "", ...(c.condicao || {}), [k]: v } } : c) });
  const toggleCond = (i, on) => setForm({ ...form, campos: form.campos.map((c, idx) => idx === i ? { ...c, condicao: on ? { campo: "", operador: "eq", valor: "" } : null } : c) });
  const addCampo = () => setForm({ ...form, campos: [...form.campos, { key: `campo${form.campos.length + 1}`, tipo: "texto", label: "", papel: "outro", obrigatorio: false, opcoes: [], condicao: null }] });
  const rmCampo = (i) => setForm({ ...form, campos: form.campos.filter((_, idx) => idx !== i) });

  const [novaCampanha, setNovaCampanha] = useState("");
  const addCampanha = () => { const v = novaCampanha.trim(); if (!v) return; if ((form.campanhas || []).includes(v)) return; setForm({ ...form, campanhas: [...(form.campanhas || []), v] }); setNovaCampanha(""); };
  const rmCampanha = (c) => setForm({ ...form, campanhas: (form.campanhas || []).filter((x) => x !== c) });

  const setRegra = (i, k, v) => setForm({ ...form, regras_fila: form.regras_fila.map((r, idx) => idx === i ? { ...r, [k]: v } : r) });
  const addRegra = () => setForm({ ...form, regras_fila: [...form.regras_fila, { produto: "", fila_id: "" }] });
  const rmRegra = (i) => setForm({ ...form, regras_fila: form.regras_fila.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Indique o nome do formulario.");
    const payload = {
      ...form,
      emails_destino: form.emails_destino.split(",").map((s) => s.trim()).filter(Boolean),
      campos: form.campos.map((c) => ({ ...c, opcoes: typeof c.opcoes === "string" ? c.opcoes.split(",").map((s) => s.trim()).filter(Boolean) : (c.opcoes || []) })),
    };
    try {
      if (form.id) await api.put(`/forms/${form.id}`, payload);
      else await api.post("/forms", payload);
      toast.success("Formulario guardado."); setShow(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => { try { await api.delete(`/forms/${id}`); toast.success("Removido."); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const viewSubs = async (f) => { try { const { data } = await api.get(`/forms/${f.id}/submissions`); setSubs({ form: f, rows: data }); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const viewAnalytics = async (f, desde = "", ate = "") => {
    try {
      const qs = new URLSearchParams();
      if (desde) qs.set("desde", desde);
      if (ate) qs.set("ate", ate);
      const { data } = await api.get(`/forms/${f.id}/analytics${qs.toString() ? `?${qs}` : ""}`);
      setAnalytics({ form: f, data, desde, ate });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveQueue = async () => {
    if (!queue.nome.trim()) return toast.error("Indique o nome da fila.");
    const payload = { nome: queue.nome, membros: queue.membros.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (queue.id) await api.put(`/forms/queues/${queue.id}`, payload);
      else await api.post("/forms/queues", payload);
      toast.success("Fila guardada."); setShowQueue(false); setQueue({ nome: "", membros: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delQueue = async (id) => { try { await api.delete(`/forms/queues/${id}`); toast.success("Fila removida."); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado."); };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = (id, camp) => `${origin}/f/${id}${camp ? `?c=${encodeURIComponent(camp)}` : ""}`;
  const qrUrl = (id, camp) => `${BACKEND}/api/public/forms/${id}/qrcode.png?base=${encodeURIComponent(origin)}${camp ? `&campanha=${encodeURIComponent(camp)}` : ""}`;
  const downloadQr = async (id, nome, camp) => {
    try {
      const r = await fetch(qrUrl(id, camp));
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `qrcode-${(nome || id).replace(/\s+/g, "-").toLowerCase()}${camp ? `-${camp.replace(/\s+/g, "-").toLowerCase()}` : ""}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Nao foi possivel descarregar o QR Code."); }
  };

  const formCols = [
    { key: "nome", label: "Nome", render: (r) => <span className="font-semibold text-navy">{r.nome}</span> },
    { key: "ativo", label: "Estado", render: (r) => <Badge value={r.ativo ? "ativo" : "inativo"} /> },
    { key: "n_submissoes", label: "Submissoes", render: (r) => r.n_submissoes ?? 0 },
    { key: "acoes", label: "", render: (r) => (
      <div className="flex gap-1">
        <button onClick={() => viewSubs(r)} data-testid={`form-subs-${r.id}`} className="text-navy hover:bg-tint rounded p-1" title="Submissoes"><Eye size={16} /></button>
        <button onClick={() => viewAnalytics(r)} data-testid={`form-analytics-${r.id}`} className="text-navy hover:bg-tint rounded p-1" title="Analitica"><BarChart3 size={16} /></button>
        <button onClick={() => setShare(r)} data-testid={`form-share-${r.id}`} className="text-navy hover:bg-tint rounded p-1" title="Pagina publica e QR Code"><QrCode size={16} /></button>
        <button onClick={() => setSnippet(r)} data-testid={`form-embed-${r.id}`} className="text-blue hover:bg-tint rounded p-1" title="Incorporar"><Code size={16} /></button>
        <button onClick={() => openEdit(r)} data-testid={`form-edit-${r.id}`} className="text-navy hover:bg-tint rounded px-2 text-sm">Editar</button>
        <button onClick={() => del(r.id)} data-testid={`form-del-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={16} /></button>
      </div>
    ) },
  ];
  const queueCols = [
    { key: "nome", label: "Fila", render: (r) => <span className="font-semibold text-navy">{r.nome}</span> },
    { key: "membros", label: "Membros (round-robin)", render: (r) => <span className="text-xs text-muted2">{(r.membros || []).join(", ") || "-"}</span> },
    { key: "acoes", label: "", render: (r) => (
      <div className="flex gap-1">
        <button onClick={() => { setQueue({ id: r.id, nome: r.nome, membros: (r.membros || []).join(", ") }); setShowQueue(true); }} className="text-navy hover:bg-tint rounded px-2 text-sm">Editar</button>
        <button onClick={() => delQueue(r.id)} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={16} /></button>
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader overline="Website" title="Formularios" action={
        tab === "forms"
          ? <Btn onClick={openNew} testid="new-form-btn"><Plus size={16} /> Novo formulario</Btn>
          : (tab === "queues"
            ? <Btn onClick={() => { setQueue({ nome: "", membros: "" }); setShowQueue(true); }} testid="new-queue-btn"><Plus size={16} /> Nova fila</Btn>
            : null)
      } />

      <div className="flex gap-1 border-b border-tint mb-6">
        {[["dashboard", "Dashboard"], ["forms", "Formularios"], ["queues", "Filas de CRM"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`forms-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-blue text-navy" : "border-transparent text-muted2"}`}>{l}</button>
        ))}
      </div>

      {tab === "dashboard" && <FormsDashboard overview={overview} desde={ovDesde} ate={ovAte} setDesde={setOvDesde} setAte={setOvAte} reload={loadOverview} onOpenAnalytics={viewAnalytics} onOpenSubs={viewSubs} allForms={forms} />}
      {tab === "forms" && <Table testid="forms-table" columns={formCols} rows={forms} />}
      {tab === "queues" && (
        <>
          <p className="text-sm text-muted2 mb-3">As filas distribuem as leads pelos comerciais em round-robin. Ligue produtos a filas nas regras de cada formulario.</p>
          <Table testid="queues-table" columns={queueCols} rows={queues} />
        </>
      )}

      {/* Builder */}
      <Modal open={show} onClose={() => setShow(false)} title={form.id ? "Editar formulario" : "Novo formulario"} testid="form-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome interno" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testid="form-nome" />
          <Field label="Titulo publico" value={form.titulo_publico} onChange={(v) => setForm({ ...form, titulo_publico: v })} testid="form-titulo" />
        </div>
        <Field label="Descricao" value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} testid="form-descricao" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cor (hex)" value={form.cor} onChange={(v) => setForm({ ...form, cor: v })} testid="form-cor" />
          <Field label="Mensagem de sucesso" value={form.mensagem_sucesso} onChange={(v) => setForm({ ...form, mensagem_sucesso: v })} testid="form-sucesso" />
        </div>

        <div>
          <label className="block text-sm font-semibold text-navy mb-1">Campos</label>
          <div className="space-y-2">
            {form.campos.map((c, i) => (
              <div key={i} className="border border-tint rounded-lg p-2 space-y-2" data-testid={`form-campo-${i}`}>
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <input value={c.label} onChange={(e) => setCampo(i, "label", e.target.value)} placeholder="Etiqueta" data-testid={`campo-label-${i}`} className="px-2 py-1.5 border border-tint rounded text-sm" />
                  <input value={c.key} onChange={(e) => setCampo(i, "key", e.target.value)} placeholder="chave" className="px-2 py-1.5 border border-tint rounded text-sm" />
                  <button onClick={() => rmCampo(i)} className="text-red-600 p-1"><Trash2 size={15} /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 items-center">
                  <select value={c.tipo} onChange={(e) => setCampo(i, "tipo", e.target.value)} data-testid={`campo-tipo-${i}`} className="px-2 py-1.5 border border-tint rounded text-sm bg-white">{TIPOS.map((t) => <option key={t}>{t}</option>)}</select>
                  <select value={c.papel} onChange={(e) => setCampo(i, "papel", e.target.value)} className="px-2 py-1.5 border border-tint rounded text-sm bg-white">{PAPEIS.map((p) => <option key={p}>{p}</option>)}</select>
                  <label className="flex items-center gap-1 text-xs text-navy"><input type="checkbox" checked={!!c.obrigatorio} onChange={(e) => setCampo(i, "obrigatorio", e.target.checked)} /> Obrigatorio</label>
                </div>
                {c.tipo === "select" && (
                  <input value={Array.isArray(c.opcoes) ? c.opcoes.join(", ") : c.opcoes} onChange={(e) => setCampo(i, "opcoes", e.target.value)} placeholder="Opcoes separadas por virgula" className="w-full px-2 py-1.5 border border-tint rounded text-sm" />
                )}
                <div className="border-t border-tint/70 pt-2">
                  <label className="flex items-center gap-1 text-xs text-navy">
                    <input type="checkbox" checked={!!c.condicao} onChange={(e) => toggleCond(i, e.target.checked)} data-testid={`campo-cond-toggle-${i}`} /> Mostrar apenas em certas condicoes
                  </label>
                  {c.condicao && (
                    <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mt-2" data-testid={`campo-cond-${i}`}>
                      <select value={c.condicao.campo || ""} onChange={(e) => setCampoCond(i, "campo", e.target.value)} className="px-2 py-1.5 border border-tint rounded text-xs bg-white">
                        <option value="">Campo...</option>
                        {form.campos.filter((_, idx) => idx !== i).map((oc) => <option key={oc.key} value={oc.key}>{oc.label || oc.key}</option>)}
                      </select>
                      <select value={c.condicao.operador || ""} onChange={(e) => setCampoCond(i, "operador", e.target.value)} className="px-2 py-1.5 border border-tint rounded text-xs bg-white">
                        {OPERADORES.filter(([v]) => v).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      {(c.condicao.operador === "eq" || c.condicao.operador === "ne") ? (
                        <input value={c.condicao.valor || ""} onChange={(e) => setCampoCond(i, "valor", e.target.value)} placeholder="valor" className="px-2 py-1.5 border border-tint rounded text-xs" />
                      ) : <div />}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button onClick={addCampo} data-testid="form-add-campo" className="mt-2 text-sm text-blue font-semibold">+ Adicionar campo</button>
        </div>

        <div className="border-t border-tint pt-3">
          <label className="block text-sm font-semibold text-navy mb-1">Encaminhamento por produto (fila de CRM)</label>
          <div className="space-y-2">
            {form.regras_fila.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`form-regra-${i}`}>
                <select value={r.produto} onChange={(e) => setRegra(i, "produto", e.target.value)} className="px-2 py-1.5 border border-tint rounded text-sm bg-white">
                  <option value="">Produto...</option>
                  {produtos.map((p) => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                </select>
                <select value={r.fila_id} onChange={(e) => setRegra(i, "fila_id", e.target.value)} className="px-2 py-1.5 border border-tint rounded text-sm bg-white">
                  <option value="">Fila...</option>
                  {queues.map((q) => <option key={q.id} value={q.id}>{q.nome}</option>)}
                </select>
                <button onClick={() => rmRegra(i)} className="text-red-600 p-1"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <button onClick={addRegra} data-testid="form-add-regra" className="mt-2 text-sm text-blue font-semibold">+ Adicionar regra</button>
          <div className="mt-2"><Selecter label="Fila por omissao (sem produto ou sem regra)" value={form.fila_default_id} onChange={(v) => setForm({ ...form, fila_default_id: v })} options={[{ value: "", label: "Nenhuma" }, ...queues.map((q) => ({ value: q.id, label: q.nome }))]} testid="form-fila-default" /></div>
        </div>

        <div className="border-t border-tint pt-3 space-y-2">
          <Field label="Emails de destino (separados por virgula)" value={form.emails_destino} onChange={(v) => setForm({ ...form, emails_destino: v })} testid="form-emails" />
          <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={!!form.auto_reply_ativo} onChange={(e) => setForm({ ...form, auto_reply_ativo: e.target.checked })} data-testid="form-autoreply-ativo" /> Enviar resposta automatica ao contacto</label>
          {form.auto_reply_ativo && <TextArea label="Texto da resposta automatica" value={form.auto_reply_texto} onChange={(v) => setForm({ ...form, auto_reply_texto: v })} testid="form-autoreply-texto" />}
          <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={!!form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} data-testid="form-ativo" /> Formulario ativo</label>
        </div>

        <div className="border-t border-tint pt-3">
          <label className="block text-sm font-semibold text-navy mb-1">Campanhas (para QR/URL e origem das leads)</label>
          <p className="text-xs text-muted2 mb-2">Crie etiquetas (ex: feira, flyer, cartaz). Cada uma gera um QR/URL proprio no botao de partilha e regista a origem das submissoes.</p>
          <div className="flex gap-2">
            <input value={novaCampanha} onChange={(e) => setNovaCampanha(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCampanha(); } }} placeholder="Nome da campanha" data-testid="form-campanha-input" className="flex-1 px-2 py-1.5 border border-tint rounded text-sm" />
            <Btn variant="ghost" onClick={addCampanha} testid="form-campanha-add"><Plus size={15} /> Adicionar</Btn>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(form.campanhas || []).map((c) => (
              <span key={c} className="inline-flex items-center gap-1 bg-tint text-navy rounded-full px-3 py-1 text-xs" data-testid={`form-campanha-chip-${c}`}>
                {c}<button onClick={() => rmCampanha(c)} className="text-red-600"><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancelar</Btn><Btn onClick={save} testid="form-save">Guardar</Btn></div>
      </Modal>

      {/* Partilhar: pagina publica + QR Code */}
      <Modal open={!!share} onClose={() => setShare(null)} title="Pagina publica e QR Code" testid="share-modal">
        {share && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">Link da pagina publica</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-tint text-navy rounded-lg p-2.5 text-[12px] break-all" data-testid="share-url">{publicUrl(share.id)}</code>
                <Btn variant="ghost" onClick={() => copy(publicUrl(share.id))} testid="share-copy"><Copy size={15} /> Copiar</Btn>
              </div>
              <a href={publicUrl(share.id)} target="_blank" rel="noreferrer" data-testid="share-open" className="inline-flex items-center gap-1 text-sm text-blue font-semibold mt-2 hover:underline"><ExternalLink size={14} /> Abrir pagina</a>
            </div>
            <div className="border-t border-tint pt-4">
              <label className="block text-sm font-semibold text-navy mb-2">QR Code (campanhas offline)</label>
              <div className="flex items-center gap-4">
                <img src={qrUrl(share.id)} alt="QR Code" data-testid="share-qr" className="w-36 h-36 border border-tint rounded-lg bg-white p-2" />
                <div className="space-y-2">
                  <p className="text-sm text-muted2">QR geral (sem campanha). Aponte a camera do telemovel para abrir o formulario.</p>
                  <Btn onClick={() => downloadQr(share.id, share.nome)} testid="share-qr-download"><Download size={15} /> Descarregar QR geral</Btn>
                </div>
              </div>
            </div>
            {(share.campanhas || []).length > 0 && (
              <div className="border-t border-tint pt-4">
                <label className="block text-sm font-semibold text-navy mb-2">Por campanha</label>
                <div className="space-y-3" data-testid="share-campanhas">
                  {(share.campanhas || []).map((c) => (
                    <div key={c} className="flex items-center gap-3 bg-tint/50 rounded-lg p-2" data-testid={`share-campanha-${c}`}>
                      <img src={qrUrl(share.id, c)} alt={`QR ${c}`} className="w-16 h-16 border border-tint rounded bg-white p-1" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-navy text-sm">{c}</div>
                        <code className="block text-[11px] text-muted2 break-all">{publicUrl(share.id, c)}</code>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => copy(publicUrl(share.id, c))} className="text-blue hover:bg-tint rounded p-1" title="Copiar URL"><Copy size={14} /></button>
                        <button onClick={() => downloadQr(share.id, share.nome, c)} className="text-navy hover:bg-tint rounded p-1" title="Descarregar QR"><Download size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(share.campanhas || []).length === 0 && (
              <p className="text-xs text-muted2 border-t border-tint pt-3">Dica: crie campanhas na edicao do formulario para gerar QR/URLs por campanha (feira, flyer, cartaz) e saber a origem das leads.</p>
            )}
          </div>
        )}
      </Modal>

      {/* Analitica */}
      <Modal open={!!analytics} onClose={() => setAnalytics(null)} title={`Analitica - ${analytics?.form?.nome || ""}`} testid="analytics-modal">
        {analytics && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Desde</label>
                <input type="date" value={analytics.desde} onChange={(e) => viewAnalytics(analytics.form, e.target.value, analytics.ate)} data-testid="analytics-desde" className="px-2 py-1.5 border border-tint rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Ate</label>
                <input type="date" value={analytics.ate} onChange={(e) => viewAnalytics(analytics.form, analytics.desde, e.target.value)} data-testid="analytics-ate" className="px-2 py-1.5 border border-tint rounded text-sm" />
              </div>
              {(analytics.desde || analytics.ate) && (
                <button onClick={() => viewAnalytics(analytics.form, "", "")} className="text-sm text-blue font-semibold pb-1.5" data-testid="analytics-clear">Limpar datas</button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3" data-testid="analytics-kpis">
              <div className="bg-tint rounded-lg p-3"><div className="text-2xl font-bold text-navy">{analytics.data.visitas}</div><div className="text-xs text-muted2">Visitas</div></div>
              <div className="bg-tint rounded-lg p-3"><div className="text-2xl font-bold text-navy">{analytics.data.submissoes}</div><div className="text-xs text-muted2">Submissoes</div></div>
              <div className="bg-tint rounded-lg p-3"><div className="text-2xl font-bold text-blue">{analytics.data.conversao_pct}%</div><div className="text-xs text-muted2">Conversao</div></div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy mb-2">Evolucao diaria</label>
              {(analytics.data.serie || []).length === 0 ? (
                <p className="text-sm text-muted2">Sem dados no periodo.</p>
              ) : (
                <div className="flex items-end gap-1 h-32 border-b border-tint pb-1 overflow-x-auto" data-testid="analytics-chart">
                  {analytics.data.serie.map((d) => {
                    const max = Math.max(1, ...analytics.data.serie.map((x) => x.visitas));
                    return (
                      <div key={d.dia} className="flex flex-col items-center gap-0.5 min-w-[26px]" title={`${d.dia}: ${d.visitas} visitas, ${d.submissoes} submissoes`}>
                        <div className="w-full flex items-end justify-center gap-0.5 h-24">
                          <div className="w-2 bg-navy/30 rounded-t" style={{ height: `${(d.visitas / max) * 100}%` }} />
                          <div className="w-2 bg-blue rounded-t" style={{ height: `${(d.submissoes / max) * 100}%` }} />
                        </div>
                        <span className="text-[9px] text-muted2">{d.dia.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-4 mt-2 text-xs text-muted2"><span className="flex items-center gap-1"><span className="w-2 h-2 bg-navy/30 inline-block rounded" /> Visitas</span><span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue inline-block rounded" /> Submissoes</span></div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy mb-2">Por campanha</label>
              <div className="space-y-1" data-testid="analytics-campanhas">
                {(analytics.data.por_campanha || []).length === 0 && <p className="text-sm text-muted2">Sem dados.</p>}
                {(analytics.data.por_campanha || []).map((c) => (
                  <div key={c.campanha} className="flex items-center justify-between bg-tint/50 rounded px-3 py-1.5 text-sm">
                    <span className="text-navy font-medium">{c.campanha}</span>
                    <span className="text-xs text-muted2">{c.visitas} visitas - {c.submissoes} submissoes</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Snippet / embed */}
      <Modal open={!!snippet} onClose={() => setSnippet(null)} title="Incorporar no website" testid="embed-modal" size="lg">
        {snippet && (() => {
          const scriptTag = `<div id="cogniseg-form-${snippet.id}"></div>\n<script async src="${BACKEND}/api/public/forms/${snippet.id}/form-widget.js"></script>`;
          const iframeTag = `<iframe src="${window.location.origin}/f/${snippet.id}" width="100%" height="600" style="border:none;border-radius:12px" title="Formulario"></iframe>`;
          const linkUrl = `${window.location.origin}/f/${snippet.id}`;
          const linkTag = `<a href="${linkUrl}" target="_blank" rel="noopener">Abrir formulario</a>`;
          return (
            <div className="space-y-4">
              <p className="text-xs text-muted2">Escolha o metodo que preferir. Todas as opcoes enviam para o mesmo endpoint e criam Lead no CRM.</p>

              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Opcao 1 - Script (widget dinamico, recomendado)</label>
                <p className="text-[11px] text-muted2 mb-1">Cole antes de <code>&lt;/body&gt;</code>. Estilo integra com o layout do site.</p>
                <div className="flex items-start gap-2">
                  <pre className="flex-1 bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] whitespace-pre-wrap break-all" data-testid="embed-snippet">{scriptTag}</pre>
                  <Btn variant="ghost" onClick={() => copy(scriptTag)} testid="embed-copy-script">Copiar</Btn>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Opcao 2 - Iframe (isolamento total)</label>
                <p className="text-[11px] text-muted2 mb-1">Melhor para sites com CSP rigido. O formulario abre em pagina propria.</p>
                <div className="flex items-start gap-2">
                  <pre className="flex-1 bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] whitespace-pre-wrap break-all" data-testid="embed-iframe">{iframeTag}</pre>
                  <Btn variant="ghost" onClick={() => copy(iframeTag)} testid="embed-copy-iframe">Copiar</Btn>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Opcao 3 - Link direto</label>
                <div className="flex items-start gap-2">
                  <pre className="flex-1 bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] whitespace-pre-wrap break-all" data-testid="embed-link">{linkUrl}</pre>
                  <Btn variant="ghost" onClick={() => copy(linkUrl)} testid="embed-copy-link">Copiar</Btn>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Endpoints (integracao a medida)</label>
                <code className="block bg-tint text-navy rounded-lg p-2 text-[11px] break-all">GET {BACKEND}/api/public/forms/{snippet.id}</code>
                <code className="block bg-tint text-navy rounded-lg p-2 text-[11px] break-all mt-1">POST {BACKEND}/api/public/forms/{snippet.id}/submit</code>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Submissoes */}
      <Modal open={!!subs} onClose={() => setSubs(null)} title={`Submissoes - ${subs?.form?.nome || ""}`} testid="subs-modal">
        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="subs-list">
          {(subs?.rows || []).length === 0 && <p className="text-sm text-muted2">Sem submissoes.</p>}
          {(subs?.rows || []).map((s, i) => (
            <div key={i} className="bg-tint rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-navy">{s.dados?.nome || s.dados?.email || "Contacto"}</span>
                <span className="text-xs text-muted2">{dt(s.em)}</span>
              </div>
              <div className="text-xs text-muted2 mt-1">{s.produto ? `Produto: ${s.produto} - ` : ""}Fila: {s.fila_nome || "-"} - Owner: {s.owner_email || "-"}</div>
              <div className="text-xs text-navy mt-1">{Object.entries(s.dados || {}).filter(([k]) => k !== "_hp" && k !== "_token" && k !== "_campanha").map(([k, v]) => `${k}: ${v}`).join(" | ")}</div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Fila */}
      <Modal open={showQueue} onClose={() => setShowQueue(false)} title={queue.id ? "Editar fila" : "Nova fila"} testid="queue-modal">
        <Field label="Nome da fila" value={queue.nome} onChange={(v) => setQueue({ ...queue, nome: v })} testid="queue-nome" />
        <Field label="Membros (emails separados por virgula)" value={queue.membros} onChange={(v) => setQueue({ ...queue, membros: v })} placeholder="comercial@cogniseg.com, ..." testid="queue-membros" />
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowQueue(false)}>Cancelar</Btn><Btn onClick={saveQueue} testid="queue-save">Guardar</Btn></div>
      </Modal>
    </>
  );
}


// -----------------------------------------------------------------------------
// FormsDashboard: painel agregado de todos os formularios (metricas + drilldown)
// -----------------------------------------------------------------------------
function FormsDashboard({ overview, desde, ate, setDesde, setAte, reload, onOpenAnalytics, onOpenSubs, allForms }) {
  const totais = overview?.totais || { formularios: 0, visitas: 0, submissoes: 0, conversao_pct: 0 };
  const itens = overview?.formularios || [];
  const serie = overview?.serie || [];
  const maxVal = Math.max(1, ...serie.map((d) => Math.max(d.visitas || 0, d.submissoes || 0)));
  const topConversao = [...itens].sort((a, b) => b.conversao_pct - a.conversao_pct).slice(0, 3);
  const topSubs = [...itens].sort((a, b) => b.submissoes - a.submissoes).slice(0, 3);

  const findForm = (id) => (allForms || []).find((f) => f.id === id);

  return (
    <div className="space-y-6" data-testid="forms-dashboard">
      {/* Filtros de periodo */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-tint bg-white p-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-muted2 mb-1">Desde</label>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="ov-desde" className="border border-tint rounded-md px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-muted2 mb-1">Ate</label>
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} data-testid="ov-ate" className="border border-tint rounded-md px-3 py-1.5 text-sm" />
        </div>
        {(desde || ate) && (
          <button onClick={() => { setDesde(""); setAte(""); }} className="text-xs text-muted2 hover:text-navy underline" data-testid="ov-clear">Limpar</button>
        )}
        <div className="ml-auto text-[11px] text-muted2">{overview ? `Actualizado: ${new Date().toLocaleTimeString("pt-PT")}` : "A carregar..."}</div>
      </div>

      {/* KPIs globais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={LayoutDashboard} accent="blue" label="Formularios" value={totais.formularios} testid="kpi-formularios" />
        <Kpi icon={MousePointerClick} accent="navy" label="Visitas" value={totais.visitas} testid="kpi-visitas" />
        <Kpi icon={CheckCircle2} accent="green" label="Submissoes" value={totais.submissoes} testid="kpi-submissoes" />
        <Kpi icon={TrendingUp} accent={totais.conversao_pct >= 30 ? "green" : totais.conversao_pct >= 10 ? "amber" : "red"} label="Conversao global" value={`${totais.conversao_pct} %`} testid="kpi-conversao" />
      </div>

      {/* Grafico simples de barras da serie temporal */}
      {serie.length > 0 && (
        <div className="rounded-lg border border-tint bg-white p-4" data-testid="ov-chart">
          <div className="text-sm font-semibold text-navy mb-3">Visitas e submissoes por dia</div>
          <div className="flex items-end gap-1 h-32">
            {serie.map((d) => {
              const vH = Math.round(((d.visitas || 0) / maxVal) * 100);
              const sH = Math.round(((d.submissoes || 0) / maxVal) * 100);
              return (
                <div key={d.dia} className="flex-1 flex items-end gap-0.5 group relative" title={`${d.dia}: ${d.visitas} visitas, ${d.submissoes} submissoes`}>
                  <div className="flex-1 bg-slate-200 rounded-t" style={{ height: `${vH}%` }} />
                  <div className="flex-1 bg-blue rounded-t" style={{ height: `${sH}%` }} />
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[11px] text-muted2">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-slate-200 rounded"></span> Visitas</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-blue rounded"></span> Submissoes</span>
          </div>
        </div>
      )}

      {/* Top 3 por conversao e submissoes */}
      <div className="grid gap-4 md:grid-cols-2">
        <TopList title="Top conversao" items={topConversao} value={(f) => `${f.conversao_pct}%`} testid="ov-top-conv" onOpen={(f) => onOpenAnalytics(findForm(f.id))} />
        <TopList title="Top submissoes" items={topSubs} value={(f) => `${f.submissoes} subs.`} testid="ov-top-subs" onOpen={(f) => onOpenSubs(findForm(f.id))} />
      </div>

      {/* Tabela detalhada por formulario */}
      <div className="rounded-lg border border-tint bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-tint flex items-center justify-between">
          <div className="text-sm font-semibold text-navy">Detalhe por formulario</div>
          <button onClick={reload} data-testid="ov-reload" className="text-xs text-blue hover:underline">Actualizar</button>
        </div>
        <table className="w-full text-sm" data-testid="ov-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-muted2">
            <tr>
              <th className="text-left px-4 py-2">Formulario</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-right px-3 py-2">Visitas</th>
              <th className="text-right px-3 py-2">Submissoes</th>
              <th className="text-right px-3 py-2">Conversao</th>
              <th className="text-left px-3 py-2">Ultima submissao</th>
              <th className="text-right px-3 py-2">Accoes</th>
            </tr>
          </thead>
          <tbody>
            {itens.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-muted2 text-xs">Sem formularios no periodo seleccionado.</td></tr>
            )}
            {itens.map((f) => (
              <tr key={f.id} className="border-t border-tint hover:bg-slate-50" data-testid={`ov-row-${f.id}`}>
                <td className="px-4 py-2">
                  <div className="font-semibold text-navy">{f.nome}</div>
                  {f.titulo_publico && <div className="text-[11px] text-muted2 truncate max-w-md">{f.titulo_publico}</div>}
                </td>
                <td className="px-3 py-2"><Badge value={f.ativo ? "ativo" : "inativo"} /></td>
                <td className="px-3 py-2 text-right text-navy">{f.visitas}</td>
                <td className="px-3 py-2 text-right text-navy font-semibold">{f.submissoes}</td>
                <td className="px-3 py-2 text-right">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${f.conversao_pct >= 30 ? "bg-green-50 text-green-700" : f.conversao_pct >= 10 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-muted2"}`}>
                    {f.conversao_pct}%
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] text-muted2">{f.ultima_submissao ? new Date(f.ultima_submissao).toLocaleString("pt-PT") : <span className="italic">nunca</span>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => onOpenSubs(findForm(f.id))} data-testid={`ov-subs-${f.id}`} className="text-navy hover:text-blue px-1" title="Ver submissoes"><Eye size={14} /></button>
                  <button onClick={() => onOpenAnalytics(findForm(f.id))} data-testid={`ov-analytics-${f.id}`} className="text-navy hover:text-blue px-1" title="Analitica detalhada"><BarChart3 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "navy", testid }) {
  const colors = {
    blue: "bg-blue/10 text-blue",
    navy: "bg-slate-100 text-navy",
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <div className="rounded-lg border border-tint bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`rounded-md p-2 ${colors[accent]}`}><Icon size={18} /></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-muted2 truncate">{label}</div>
          <div className="text-2xl font-heading font-bold text-navy">{value}</div>
        </div>
      </div>
    </div>
  );
}

function TopList({ title, items, value, testid, onOpen }) {
  return (
    <div className="rounded-lg border border-tint bg-white p-4" data-testid={testid}>
      <div className="text-sm font-semibold text-navy mb-3">{title}</div>
      {items.length === 0 && <div className="text-xs text-muted2">Sem dados no periodo.</div>}
      <div className="space-y-2">
        {items.map((f, i) => (
          <button
            key={f.id}
            type="button"
            onClick={() => onOpen && onOpen(f)}
            data-testid={`${testid}-${i}`}
            className="w-full flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-slate-50 text-left"
          >
            <div className="w-6 h-6 rounded-full bg-blue/10 text-blue text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-navy truncate">{f.nome}</div>
              <div className="text-[11px] text-muted2">{f.submissoes} submissoes - {f.visitas} visitas</div>
            </div>
            <div className="text-sm font-bold text-navy shrink-0">{value(f)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
